import React, { useState } from "react";
import { usePricingSettings } from "../../contexts/PricingSettingsContext";
import { apiFetch } from "../../lib/apiFetch";

export function PricingCoachPanel({ coach, currency = "$" }) {
  const [aiCoach, setAiCoach] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const { pricingSettings } = usePricingSettings();
  const aiEnabled = pricingSettings?.enablePricingAi !== false;

  if (!coach || !coach.ok) {
    return (
      <div className="mudo-card p-4 text-sm space-y-1">
        <h3 className="mudo-section-title">Pricing &amp; margin coach</h3>
        <p className="text-xs opacity-70">
          Once you’ve added your materials, staff, kiln and packaging costs, Mudo will suggest wholesale and retail
          prices to hit your target margins.
        </p>
      </div>
    );
  }

  const { unitCost, retailPrice, wholesalePrice, notes } = coach;
  const targetWholesalePct = Math.round((coach.targetWholesaleMargin ?? 0.5) * 100);
  const targetRetailPct = Math.round((coach.targetRetailMargin ?? 0.65) * 100);

  async function handleAskAi() {
    if (!coach || !coach.ok) return;
    if (!aiEnabled) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const context = {
        currency,
        unitCost: coach.unitCost,
        suggestedRetail: coach.retailPrice,
        suggestedWholesale: coach.wholesalePrice,
        userEnteredPrices: coach.userPrices || null,
        targetRetailMargin: coach.targetRetailMargin,
        targetWholesaleMargin: coach.targetWholesaleMargin,
      };
      const res = await apiFetch("/api/ai/coach", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "pricing", context }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error || "AI error");
      setAiCoach(json.result);
    } catch (err) {
      console.error("[PricingCoachPanel] AI error", err);
      setAiError("Mudo AI couldn’t explain this right now.");
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <div className="mudo-card p-4 text-sm space-y-3">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="mudo-section-title">Pricing &amp; margin coach</h3>
        <div className="text-xs opacity-70">
          True cost: <span className="font-mono">{currency}{unitCost.toFixed(2)}</span> / piece
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2">
          <div className="opacity-70">Suggested wholesale</div>
          <div className="text-lg font-semibold">
            {currency}{wholesalePrice.toFixed(2)}
          </div>
          <div className="opacity-60 mt-1">
            Leaves ~{targetWholesalePct}% margin after costs.
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2">
          <div className="opacity-70">Suggested retail</div>
          <div className="text-lg font-semibold">
            {currency}{retailPrice.toFixed(2)}
          </div>
          <div className="opacity-60 mt-1">
            Targets ~{targetRetailPct}% margin per piece.
          </div>
        </div>
      </div>

      {notes?.length > 0 && (
        <ul className="text-xs opacity-80 space-y-1 list-disc list-inside">
          {notes.map((line, idx) => (
            <li key={idx}>{line}</li>
          ))}
        </ul>
      )}

      <div className="border border-dashed border-cyan-500/40 rounded-xl px-3 py-2 text-[11px] space-y-1 mt-2">
        <div className="flex items-center justify-between">
          <span className="uppercase tracking-wide opacity-70">Mudo AI</span>
          <button
            type="button"
            onClick={handleAskAi}
            disabled={aiLoading || !aiEnabled}
            className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 bg-black/40 hover:bg-black/60 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {aiLoading ? "Thinking…" : "Explain this pricing"}
          </button>
        </div>
        {aiError && <p className="text-[11px] text-red-300 mt-1">{aiError}</p>}
        {aiCoach && !aiError && (
          <div className="mt-1 space-y-1">
            <div className="font-medium opacity-80">{aiCoach.title}</div>
            <p className="opacity-80">{aiCoach.summary}</p>
            {Array.isArray(aiCoach.bullets) && aiCoach.bullets.length > 0 && (
              <ul className="list-disc list-inside opacity-80 space-y-0.5">
                {aiCoach.bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
